import axios from "axios";

export function resolveApiBaseURL(
  configuredBaseURL: string | undefined,
  nodeEnv: string | undefined,
  currentOrigin: string
) {
  const configured = String(configuredBaseURL || "").trim();
  if (configured) {
    return configured.replace(/\/+$/, "");
  }

  const normalizedOrigin = currentOrigin.replace(/\/+$/, "");
  if (nodeEnv === "production") {
    return normalizedOrigin;
  }

  const developmentURL = new URL(normalizedOrigin);
  developmentURL.port = "3000";
  return developmentURL.origin;
}

export const baseURL: string = resolveApiBaseURL(
  process.env.REACT_APP_API_BASE_URL,
  process.env.NODE_ENV,
  window.location.origin
);

export function resolveAssetURL(
  assetURL: string | undefined,
  assetBaseURL: string = baseURL
) {
  const normalizedURL = String(assetURL || "").trim();
  if (!normalizedURL || /^https?:\/\//i.test(normalizedURL)) {
    return normalizedURL;
  }
  return `${assetBaseURL.replace(/\/+$/, "")}/${normalizedURL.replace(
    /^\/+/,
    ""
  )}`;
}

export const API_REQUEST_TIMEOUT_MS = 30_000;

const AUTH_STORAGE_KEY = "auth_session";
export const AUTH_INVALID_EVENT = "project-management-auth-invalid";

export interface AuthSession {
  user?: {
    username?: string;
    mail?: string;
    phone?: string;
    nickname?: string;
    avatar?: string;
    department?: string[];
    team_code?: string;
    link_code?: string;
    role?: number;
  };
  user_type?: "team_admin" | "user";
  session_id?: string;
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
}

export function getAuthSession(): AuthSession | null {
  const savedSession = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!savedSession) {
    return null;
  }

  try {
    return JSON.parse(savedSession);
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export function clearAuthStorage() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function notifyAuthInvalid(message?: string) {
  window.dispatchEvent(
    new CustomEvent(AUTH_INVALID_EVENT, {
      detail: { message: message || "登录状态已失效，请重新登录" },
    })
  );
}

export function saveAuthStorage(data: AuthSession) {
  const currentSession = getAuthSession();
  const nextSession: AuthSession = {
    ...currentSession,
    ...data,
    user: data.user || currentSession?.user,
    session_id: data.session_id || currentSession?.session_id,
  };

  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
}

async function refreshAccessToken() {
  const authSession = getAuthSession();
  if (!authSession?.session_id || !authSession?.refresh_token) {
    clearAuthStorage();
    return false;
  }

  const { data } = await axios.post(
    `${baseURL}/user/refreshToken`,
    {
      session_id: authSession.session_id,
      refresh_token: authSession.refresh_token,
    },
    { timeout: API_REQUEST_TIMEOUT_MS }
  );

  if (data.code !== 0) {
    clearAuthStorage();
    return false;
  }

  saveAuthStorage(data.data || {});
  return true;
}

const createApi = () => {
  const authSession = getAuthSession();
  const userId: string = authSession?.user?.username || "";
  const token: string = authSession?.access_token || "";
  const headers: Record<string, string> = {};
  if (userId) {
    headers.userId = userId;
  }
  if (token) {
    headers.token = token;
    headers.Authorization = `Bearer ${token}`;
  }

  let cli = axios.create({
    baseURL,
    headers,
    timeout: API_REQUEST_TIMEOUT_MS,
  });

  cli.interceptors.response.use(async (res) => {
    if (res.data.code === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed && res.config) {
        const nextToken = getAuthSession()?.access_token || "";
        res.config.headers = {
          ...(res.config.headers || {}),
          token: nextToken,
          Authorization: `Bearer ${nextToken}`,
        };
        return cli.request(res.config);
      }
      notifyAuthInvalid(res.data.message);
    }
    return res;
  });

  return cli;
};

export default createApi;
